import {
  createDeleteAtom,
  createMutationAtom,
  CreateResourceAtom,
} from 'atoms/create-resource-atom';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { atom } from 'jotai';
import { atomFamily, atomWithReset, loadable } from 'jotai/utils';
import { createResourceAtom } from 'lib/atoms';
import request from 'lib/request';
import { useMemo } from 'react';
import { ApiPayloads } from 'types/api-payloads';
import {
  CapacityResponse,
  InventoryResponse,
  OwnershipResponse,
  PackagesFromEntityResponse,
  CRMAccountResult,
  CRMTractsAndSubtractsResult,
  SalesforceAccountResponse,
  DeleteEntityLinkResponse,
  EntityLinkResponse,
  SlotNRIResponse,
  SlotSummaryResponse,
} from 'types/api-responses';
import { PackageResponse } from 'types/packages-api/responses';
import {
  mapSlotNRIResponseToTractNRI,
  mapSlotNRIResponseToSlotNRI,
} from 'utils/misc';
import { wellStatusCounts } from 'utils/slot-categories';

interface AccountEnums {
  accountType: Array<{ value: string; label: string }>;
  accountSource: Array<{ value: string; label: string }>;
}

const aliasesMutationAtom = createMutationAtom<null, 'fromAliases'>();
const createAliasAtom = atom<
  CreateResourceAtom<null, ApiPayloads['fromAliases']['error']>,
  [
    {
      url: string;
      entityId: number;
      // Supplied on the retry after a consolidation-risk override prompt.
      overrideRationale?: string;
      falsePositiveIds?: number[];
      onSuccess: () => void;
      onError: (error?: ApiPayloads['fromAliases']['error']) => void;
    },
  ],
  void
>(
  (get) => get(aliasesMutationAtom),
  (
    get,
    set,
    { url, entityId, overrideRationale, falsePositiveIds, onSuccess, onError }
  ) => {
    set(aliasesMutationAtom, {
      url,
      type: 'POST',
      data: {
        toId: entityId,
        ...(overrideRationale ? { override_rationale: overrideRationale } : {}),
        ...(falsePositiveIds?.length
          ? { false_positive_entity_ids: falsePositiveIds }
          : {}),
      },
      onSuccess: () => {
        onSuccess();
      },
      onError: (errors) => {
        onError(errors);
      },
    });
  }
);

const crmAccountsAtom = createMutationAtom<CRMAccountResult, 'crmAccounts'>();

const crmTractsAtom = createMutationAtom<
  CRMTractsAndSubtractsResult,
  'crmTracts'
>();

const deleteAliasAtom = createDeleteAtom();
const deleteEntityLinkMutationAtom = createMutationAtom<
  DeleteEntityLinkResponse,
  'entityLinks'
>();
const aliasCreatedAtom = atomWithReset(false);

const capacityMutationAtom = createMutationAtom<
  CapacityResponse,
  'capacities'
>();
const createCapacityAtom = atom<
  CreateResourceAtom<CapacityResponse, ApiPayloads['capacities']['error']>,
  [
    {
      url: string;
      subjectId: number;
      predicate: string;
      objectId: number;
      onSuccess: () => void;
      onError: (error?: ApiPayloads['capacities']['error']) => void;
    },
  ],
  void
>(
  (get) => get(capacityMutationAtom),
  (get, set, { url, subjectId, predicate, objectId, onSuccess, onError }) => {
    set(capacityMutationAtom, {
      url,
      type: 'POST',
      data: {
        subjectId,
        predicate,
        objectId,
      },
      onSuccess: () => {
        onSuccess();
      },
      onError: (errors) => {
        onError(errors);
      },
    });
  }
);
const capacityCreatedAtom = atomWithReset(false);

const useAssetsAtom = (href: string) =>
  useMemo(() => createResourceAtom<OwnershipResponse>(href), [href]);
const usePackagesAtom = (href: string) =>
  useMemo(() => createResourceAtom<PackagesFromEntityResponse[]>(href), [href]);
const DEFAULT_PAGE_SIZE = 60;

type InventorySearchParams = {
  asOfDate?: string;
  includeChildren?: boolean;
} & SearchParams;

const useSearchInventoryAtom = (href: string, todayDate: string) => {
  return useMemo(() => {
    return searchAtomPair<InventoryResponse, InventorySearchParams>(
      { href },
      {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        asOfDate: todayDate,
        includeChildren: true,
      }
    );
  }, [href, todayDate]);
};

const entityPackageMutationAtom = createMutationAtom<
  PackageResponse,
  'entityPackage'
>();

const accountsSearchAtomPair =
  searchAtomPair<SalesforceAccountResponse>('salesforceAccounts');

const accountEnumsAtom = atom(async () => {
  const response = await request.get<AccountEnums>('/crm/enums');
  return response as AccountEnums;
});

export const accountEnumsLoadableAtom = loadable(accountEnumsAtom);

const entityLinkingMutationAtom = createMutationAtom<
  EntityLinkResponse,
  'entityLinks'
>();

// Atom for Slots table - fetches from /entities/:id/slots
const entitySlotsAtom = atomFamily((entityId: number) =>
  atom(async () => {
    const response = await request.get<SlotNRIResponse>(
      `/entities/${entityId}/slot_allocations`
    );
    if ('error' in response) {
      throw new Error(response.error ?? 'Error loading slots');
    }
    return response;
  })
);

// Atom for Slot Allocations table - fetches from /entities/:id/slot_allocations
const entitySlotAllocationsAtom = atomFamily((entityId: number) =>
  atom(async () => {
    const response = await request.get<SlotNRIResponse>(
      `/entities/${entityId}/slot_allocations`
    );
    if ('error' in response) {
      throw new Error(response.error ?? 'Error loading slot allocations');
    }
    return response;
  })
);

// Shared: transforms to TractNRI format (no loadable)
const entityTractNriDataAtom = atomFamily((entityId: number) =>
  atom(async (get) => {
    const response = await get(entitySlotAllocationsAtom(entityId));
    return mapSlotNRIResponseToTractNRI(response);
  })
);

// Slot allocations tab - wraps shared atom in loadable for UI
const entityTractNriAtom = atomFamily((entityId: number) =>
  loadable(entityTractNriDataAtom(entityId))
);

const entitySlotNriAtom = atomFamily((entityId: number) =>
  loadable(
    atom(async (get) => {
      const response = await get(entitySlotsAtom(entityId));
      return mapSlotNRIResponseToSlotNRI(response);
    })
  )
);

// Base atom - fetches and caches raw slot_summary API response
const entitySlotSummaryResponseAtom = atomFamily((entityId: number) =>
  atom(async () => {
    const response = await request.get<SlotSummaryResponse>(
      `/entities/${entityId}/slot_summary`
    );
    if ('error' in response) {
      throw new Error(response.error ?? 'Error loading slot summary');
    }
    return response;
  })
);

// Derived atom for slots by formation
const entitySlotsByFormationAtom = atomFamily((entityId: number) =>
  loadable(
    atom(async (get) => {
      const response = await get(entitySlotSummaryResponseAtom(entityId));
      return response.slotsByFormation;
    })
  )
);

// Base slot summary atom - returns rows without verification counts
// Used with SlotAllocationsSummary/SlotsSummary wrappers
const entitySlotSummaryAtom = atomFamily((entityId: number) =>
  loadable(
    atom(async (get) => {
      const response = await get(entitySlotSummaryResponseAtom(entityId));
      return (
        response.slotsSummary?.rows?.map((row) => ({
          wellType: row.unitType,
          producing: row.pdp,
          duc: row.duc,
          permit: row.permit,
          ltd: row.ltd,
          shutIn: row.shutIn,
          inactive: row.inactive,
          ...wellStatusCounts(row),
          expiredPermit: row.expiredPermit,
          wellboreExcluded: row.wellboreExcluded ?? 0,
          total: row.total,
          slotVerification: row.slotVerification,
          nriVerification: row.nriVerification,
          excludedSlotVerification: row.excludedSlotVerification,
          excludedNriVerification: row.excludedNriVerification,
        })) ?? []
      );
    })
  )
);

// Combined atom for EntitySlotSummary - fetches both slot summary and tract NRI together
const entitySlotSummaryDataAtom = atomFamily((entityId: number) =>
  loadable(
    atom(async (get) => {
      const [slotSummaryResponse, slotAllocationsResponse] = await Promise.all([
        get(entitySlotSummaryResponseAtom(entityId)),
        get(entitySlotAllocationsAtom(entityId)),
      ]);

      const slotSummary =
        slotSummaryResponse.slotsSummary?.rows?.map((row) => ({
          wellType: row.unitType,
          producing: row.pdp,
          duc: row.duc,
          permit: row.permit,
          ltd: row.ltd,
          shutIn: row.shutIn,
          inactive: row.inactive,
          ...wellStatusCounts(row),
          expiredPermit: row.expiredPermit,
          wellboreExcluded: row.wellboreExcluded ?? 0,
          total: row.total,
          slotVerification: row.slotVerification,
          nriVerification: row.nriVerification,
          excludedSlotVerification: row.excludedSlotVerification,
          excludedNriVerification: row.excludedNriVerification,
        })) ?? [];

      const slots = mapSlotNRIResponseToSlotNRI(slotAllocationsResponse);

      return { slotSummary, slots };
    })
  )
);

export {
  DEFAULT_PAGE_SIZE,
  aliasCreatedAtom,
  capacityCreatedAtom,
  createAliasAtom,
  createCapacityAtom,
  deleteAliasAtom,
  deleteEntityLinkMutationAtom,
  entityPackageMutationAtom,
  useAssetsAtom,
  usePackagesAtom,
  useSearchInventoryAtom,
  crmAccountsAtom,
  crmTractsAtom,
  accountsSearchAtomPair,
  entityLinkingMutationAtom,
  entityTractNriAtom,
  entitySlotNriAtom,
  entitySlotSummaryAtom,
  entitySlotsByFormationAtom,
  entitySlotSummaryDataAtom,
};
